// Iter 110 · Post-UAT enhancement — HR Leave Administration Setup Checklist.
//
// A shared card that renders the initialization posture of the Leave
// Administration module and guides a first-time operator through the
// prerequisite steps (Fiscal Year → Policies → Linked Employees →
// Bulk-Provisioning → Opening Balances → Adjustment Reasons).
//
// Two rendering modes controlled by `variant`:
//   • "banner"  — full checklist card. Used on empty-state grids so the
//                 first-time HR admin sees exactly what to do next.
//   • "compact" — collapsible one-line summary with a "Show setup steps"
//                 toggle. Used on healthy pages as a persistent
//                 discoverability hint.
//
// Consumes `GET /api/leave/admin/setup-status` (5-line probe returning
// per-step status + a `next_action_key`).

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, WarningCircle, Circle, ArrowRight, CaretDown, CaretRight, ListChecks } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const STATUS_TONE = {
    done: { icon: CheckCircle, tone: "text-emerald-600", label: "Done", pill: "bg-emerald-100 text-emerald-800" },
    pending: { icon: Circle, tone: "text-amber-600", label: "Pending", pill: "bg-amber-100 text-amber-800" },
    optional: { icon: Circle, tone: "text-slate-400", label: "Optional", pill: "bg-slate-100 text-slate-600" },
    blocked: { icon: WarningCircle, tone: "text-rose-600", label: "Blocked", pill: "bg-rose-100 text-rose-800" },
};

/**
 * @param {{
 *   variant?: "banner" | "compact",
 *   forceOpen?: boolean,          // For "compact" — start expanded
 *   hideWhenComplete?: boolean,   // When true + overall=complete → render null (used by empty-state pages)
 *   className?: string,
 * }} props
 */
export default function LeaveAdminSetupChecklist({ variant = "banner", forceOpen = false, hideWhenComplete = false, className = "" }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(forceOpen);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/leave/admin/setup-status");
            setStatus(data);
        } catch (err) {
            // If the caller doesn't have leave:admin they'll 403. Silently no-op.
            if (err?.response?.status !== 403) {
                toast.error(formatApiError(err, "Failed to check setup status"));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return null;
    if (!status) return null;

    const overall = status.overall_status;
    const nextKey = status.next_action_key;
    const nextStep = (status.steps || []).find((s) => s.key === nextKey);

    // Callers using this on an empty-state grid can pass hideWhenComplete;
    // when the setup is complete and the parent explicitly opts out, we
    // stop rendering entirely so we don't clutter healthy pages.
    if (hideWhenComplete && overall === "complete") return null;

    // ── Compact mode ─────────────────────────────────────────────
    if (variant === "compact") {
        // Nothing to show when complete + operator hasn't asked to expand.
        if (overall === "complete" && !expanded) return null;
        const pillTone =
            overall === "complete" ? "bg-emerald-100 text-emerald-800" :
            overall === "not_started" ? "bg-rose-100 text-rose-800" :
            "bg-amber-100 text-amber-800";
        const pendingCount = (status.steps || []).filter((s) => s.required && s.status !== "done").length;
        return (
            <div
                className={`bg-white rounded-xl border border-slate-200 ${className}`}
                data-testid="leave-setup-checklist-compact"
            >
                <button
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 rounded-xl"
                    onClick={() => setExpanded(!expanded)}
                    data-testid="setup-checklist-toggle"
                >
                    <div className="flex items-center gap-2">
                        <ListChecks size={18} className="text-slate-500" />
                        <span className="text-sm font-medium text-slate-800">Leave Administration Setup</span>
                        <Badge className={`text-xs ${pillTone}`}>
                            {overall === "complete" ? "Complete" : `${pendingCount} step${pendingCount === 1 ? "" : "s"} pending`}
                        </Badge>
                    </div>
                    {expanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                </button>
                {expanded && <div className="px-4 pb-4"><StepList steps={status.steps} nextKey={nextKey} onReload={load} /></div>}
            </div>
        );
    }

    // ── Banner (full) mode ──────────────────────────────────────
    return (
        <div
            className={`bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border border-indigo-200 rounded-xl p-5 ${className}`}
            data-testid="leave-setup-checklist-banner"
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-white/80 border border-indigo-100">
                        <ListChecks size={22} className="text-indigo-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">Set up HR Leave Administration</h3>
                        <p className="text-xs text-slate-600 mt-0.5 max-w-xl leading-relaxed">
                            Complete these prerequisites in order. Each step deep-links to the right screen; the checklist updates automatically as you make progress.
                        </p>
                    </div>
                </div>
                <Badge className={
                    overall === "complete" ? "bg-emerald-100 text-emerald-800" :
                    overall === "not_started" ? "bg-rose-100 text-rose-800" :
                    "bg-amber-100 text-amber-800"
                }>
                    {overall === "complete" ? "Complete" : overall === "not_started" ? "Not started" : "In progress"}
                </Badge>
            </div>
            {nextStep && (
                <div className="mb-4 p-3 rounded-lg bg-white border border-indigo-100" data-testid="setup-checklist-next-action">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Next action</div>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900">{nextStep.label}</div>
                            {nextStep.next_action && (
                                <div className="text-xs text-slate-700 mt-1 leading-relaxed">{nextStep.next_action}</div>
                            )}
                            {nextStep.hint && (
                                <div className="text-[11px] text-slate-500 mt-1 italic leading-relaxed">{nextStep.hint}</div>
                            )}
                        </div>
                        <StepCTA step={nextStep} onReload={load} />
                    </div>
                </div>
            )}
            <StepList steps={status.steps} nextKey={nextKey} onReload={load} />
        </div>
    );
}

function StepList({ steps, nextKey, onReload }) {
    return (
        <ol className="space-y-1.5" data-testid="setup-checklist-steps">
            {(steps || []).map((step) => {
                const tone = STATUS_TONE[step.status] || STATUS_TONE.pending;
                const StatusIcon = tone.icon;
                const isNext = step.key === nextKey;
                // Per-step operator guidance is shown for anything that
                // still needs attention. Completed steps stay compact.
                const showGuidance = step.status !== "done";
                return (
                    <li
                        key={step.key}
                        data-testid={`setup-step-${step.key}`}
                        className={
                            "flex flex-col gap-2 px-3 py-2 rounded-lg border " +
                            (isNext ? "bg-indigo-50/60 border-indigo-200" : "bg-white/60 border-slate-100")
                        }
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <StatusIcon size={18} className={tone.tone} weight={step.status === "done" ? "fill" : "regular"} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={"text-sm font-medium " + (step.status === "done" ? "text-slate-500 line-through" : "text-slate-800")}>
                                            {step.label}
                                        </span>
                                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${tone.pill}`}>
                                            {tone.label}
                                        </span>
                                        {!step.required && <span className="text-[10px] text-slate-400 italic">optional</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                                        <span data-testid={`setup-step-count-${step.key}`}>
                                            {Number(step.count).toLocaleString()} of {Number(step.total || 0).toLocaleString()}
                                        </span>
                                        {step.auto_seeded && <span className="text-[10px] text-emerald-600">· auto-seeded</span>}
                                    </div>
                                </div>
                            </div>
                            <StepCTA step={step} onReload={onReload} />
                        </div>
                        {showGuidance && (step.hint || step.next_action) && (
                            <div
                                className="ml-7 border-l-2 border-indigo-100 pl-3 space-y-1.5"
                                data-testid={`setup-step-guidance-${step.key}`}
                            >
                                {step.hint && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Why required</div>
                                        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{step.hint}</div>
                                    </div>
                                )}
                                {step.next_action && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Recommended action</div>
                                        <div className="text-xs text-slate-700 mt-0.5 leading-relaxed">{step.next_action}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

function StepCTA({ step, onReload }) {
    // Deep-linked deep-in-tab (`?tab=…`) navigation.
    const to = step.goto_tab ? `${step.goto}?tab=${step.goto_tab}` : step.goto;
    return (
        <Link to={to} onClick={() => setTimeout(onReload, 800)}>
            <Button size="sm" variant={step.status === "done" ? "ghost" : "outline"} data-testid={`setup-step-cta-${step.key}`}>
                {step.cta_label || "Open"} <ArrowRight size={12} className="ml-1" />
            </Button>
        </Link>
    );
}
